import type { BackendModule, ReadCallback, ResourceKey, Services } from "i18next";
import { LocalStorageKeys } from "utils/storageKeys";
import { HTTP_CODES } from "../restUtil";
import { logError } from "../logging";

type BackendOptions = {
  loadPath: string;
};

type StoredTranslations = {
  translations: ResourceKey | null;
  lastModified: string;
};

export class I18NextBackend implements BackendModule<BackendOptions> {
  public type = "backend" as const;

  private options = { loadPath: "" };

  init(_services: Services, options: BackendOptions) {
    this.options = { ...this.options, ...options };
  }

  async read(lang: string, namespace: string, callback: ReadCallback) {
    let cached, fetched;
    try {
      cached = this.getCachedTranslations(lang);
      fetched = await this.fetchTranslations(lang, namespace, cached?.lastModified);
      if (fetched) {
        this.setCachedTranslations(fetched, lang);
      }
    } catch (error) {
      logError(getErrorMessage(error, lang, namespace));
    }

    const data = fetched?.translations ?? cached?.translations;
    const error = data ? null : "Failed to read translations";
    callback(error, data);
  }

  setCachedTranslations(data: StoredTranslations, lang: string) {
    localStorage.setItem(
      LocalStorageKeys.translationLastModified(lang),
      JSON.stringify(data)
    );
  }

  getCachedTranslations(lang: string) {
    try {
      const cachedTranslations = localStorage.getItem(
        LocalStorageKeys.translationLastModified(lang)
      );
      if (cachedTranslations) {
        return JSON.parse(cachedTranslations) as StoredTranslations;
      }
    } catch (error) {
      logError("Error reading cached translations", error);
    }
  }

  async fetchTranslations(lang: string, namespace: string, lastModified?: string) {
    const url = this.options.loadPath
      .replace("{{lng}}", lang)
      .replace("{{ns}}", namespace);
    const headers = {
      "If-Modified-Since": lastModified ?? "",
    };
    const response = await fetch(url, { headers });

    if (response.status === HTTP_CODES.NOT_MODIFIED) {
      return null;
    } else if (!response.ok) {
      throw new Error(
        `Failed to load UI translations for ${lang}/${namespace}: ` +
          `${response.status} ${response.statusText}`
      );
    }

    const contentType = response.headers.get("Content-Type")?.toLowerCase();
    const responseText = await response.text();
    try {
      return {
        translations: JSON.parse(responseText) as ResourceKey,
        lastModified: response.headers.get("Last-Modified")!,
      };
    } catch {
      throw new Error(
        `Failed to load UI translations for ${lang}/${namespace}: ` +
          `expected JSON from ${url}, got ${getResponseType(contentType, responseText)}`
      );
    }
  }
}

function getErrorMessage(error: unknown, lang: string, namespace: string) {
  if (error instanceof Error) {
    return error.message;
  }

  return `Failed to load UI translations for ${lang}/${namespace}`;
}

function getResponseType(contentType?: string, responseText?: string) {
  const normalizedResponseText = responseText?.trimStart().toLowerCase() ?? "";
  if (
    contentType?.includes("text/html") ||
    normalizedResponseText.startsWith("<!doctype html") ||
    normalizedResponseText.startsWith("<html")
  ) {
    return "HTML";
  }

  return contentType ?? "non-JSON content";
}
